/** @file TransportBar: play / pause, frame, speed, loop, the mirrorHalf badge and the pose buttons. */
import type { ReactElement } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { NumberInput } from '../inspector/NumberInput';
import { mirrorGuard } from './mirrorGuard';
import { mirrorStart } from './mirrorStart';
import { usePlayheadClip } from './usePlayheadClip';

const SPEEDS = [0.25, 0.5, 1, 2];
const btn = { font: 'inherit', fontSize: 11, padding: '1px 6px' };

/** Buttons run the same handlers as the timeline's keys (`timelineHandlers`); Mirror L<->R is `clip.mirrorPose`. */
export function TransportBar({ handlers }: { handlers: Record<string, () => void> }): ReactElement | null {
  const store = useEditorStore();
  const cur = usePlayheadClip();
  const playhead = useEditor((s) => s.playhead);
  if (cur === null) return null;
  const run = (id: string) => (): void => { handlers[id]?.(); };
  const start = mirrorStart(cur.clip);
  const mirrorLR = (): void => {
    const s = store.getState();
    if (!mirrorGuard(cur.clip, playhead.frame, s.notify)) void s.dispatch([{ name: 'clip.mirrorPose', input: { clip: cur.name, frame: playhead.frame } }], 'mirror pose');
  };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 4px', fontSize: 11, flexWrap: 'wrap' }}>
      <button type="button" style={btn} aria-label={playhead.playing ? 'pause' : 'play'} onClick={run('timeline.playPause')}>{playhead.playing ? '❚❚' : '▶'}</button>
      <NumberInput label="playhead frame" value={playhead.frame} step={1} width={44}
        onCommit={(f) => { store.getState().setPlayhead({ frame: Math.min(cur.clip.frames, Math.max(0, Math.round(f))), playing: false }); }} />
      <span style={{ color: 'var(--muted, #8b919e)' }}>/ {cur.clip.frames}</span>
      <select aria-label="speed" value={playhead.speed} onChange={(e) => { store.getState().setPlayhead({ speed: Number(e.currentTarget.value) }); }}>
        {SPEEDS.map((v) => <option key={v} value={v}>{v}x</option>)}
      </select>
      <label><input type="checkbox" checked={playhead.loop} onChange={(e) => { store.getState().setPlayhead({ loop: e.currentTarget.checked }); }} /> loop</label>
      {start !== null && <span title="frames from here mirror the first half" style={{ color: 'var(--info, #7fd1ff)' }}>mirror half from {start}</span>}
      <span style={{ flex: 1 }} />
      <button type="button" style={btn} onClick={run('timeline.keyPose')}>Key pose</button>
      <button type="button" style={btn} onClick={run('timeline.keyAll')}>Key all</button>
      <button type="button" style={btn} onClick={run('timeline.copyPose')}>Copy pose</button>
      <button type="button" style={btn} onClick={run('timeline.pastePose')}>Paste pose</button>
      <button type="button" style={btn} onClick={mirrorLR}>Mirror L↔R</button>
    </div>
  );
}
